import { ApiService } from 'src/app/services/api.service'
import { ChangeDto, SyncPairAccessDto, SyncPairInfo } from 'src/app/services/dtos'
import { Subscription } from 'rxjs'
import { UiService } from 'src/app/services/ui.service'
import { finalize } from 'rxjs/operators'
import { handleArpioError } from 'src/utils/handle-error'
import { subscriptionActive } from 'src/utils/rxjs-utils'

export enum AccessScope {
  SOURCE = 'source',
  TARGET = 'target',
  FAILBACK = 'failback',
}

export enum AccessState {
  TESTING,
  UPDATE_TEMPLATE,
  SUCCESS,
  FAILED,
}

export class AccessService {
  private testCount = 0
  public subscription: Subscription = null
  private hasAccess = false
  private configValid = false
  private stackName: string = null
  private isLatest = false
  private version: number = null
  private _managedByStackSet = false
  public missingChanges?: ChangeDto[] = null
  private _hasApiError = false

  constructor(
    private apiService: ApiService,
    private uiService: UiService,
    private requestDto: SyncPairInfo,
    private type: AccessScope,
    initialResponse: SyncPairAccessDto,
    /**
     * The dialog whose Test Access button drives this instance, so a failed check renders
     * there instead of the corner. Omitted by page-level callers, which correctly toast.
     */
    private dialogId?: string
  ) {
    if (initialResponse) {
      this.updateStatus(initialResponse)
    }
  }

  public updateStatus(response: SyncPairAccessDto) {
    this.missingChanges = response.missingChanges

    switch (this.type) {
      case AccessScope.SOURCE:
        this.hasAccess = response.sourceAccess
        this.configValid = response.sourceConfigValid
        this.stackName = response.sourceCloudFormationAccessStackName
        this.isLatest = response.sourceIsLatest
        this.version = response.sourceVersion
        this._managedByStackSet = response.sourceTemplateManagedByStackSet ?? false
        return
      case AccessScope.TARGET:
        this.hasAccess = response.targetAccess
        this.configValid = response.targetConfigValid
        this.stackName = response.targetCloudFormationAccessStackName
        this.isLatest = response.targetIsLatest
        this.version = response.targetVersion
        this._managedByStackSet = response.targetTemplateManagedByStackSet ?? false
        return
      case AccessScope.FAILBACK:
        this.hasAccess = response.failbackAccess
        this.configValid = response.failbackConfigValid
        this.stackName = response.failbackCloudFormationAccessStackName
        this.isLatest = response.failbackIsLatest
        this.version = response.failbackVersion
        this._managedByStackSet = response.failbackTemplateManagedByStackSet ?? false
        return
    }
  }

  get allGood(): boolean {
    return this.hasAccess && this.isLatest && this.configValid
  }

  get hasLatestVersion(): boolean {
    return this.isLatest
  }

  // Access works and the deployed config is valid, ignoring whether the access
  // template is the latest version.  Callers that let customers proceed on an
  // older template use this so a broken or drifted endpoint still blocks.
  get accessUsable(): boolean {
    return this.hasAccess && this.configValid
  }

  get hasValidConfig(): boolean {
    return this.configValid
  }

  get versionNumber(): number {
    return this.version
  }

  get stack(): string {
    return this.stackName
  }

  get managedByStackSet(): boolean {
    return this._managedByStackSet
  }

  get tests(): number {
    return this.testCount
  }

  get hasApiError(): boolean {
    return this._hasApiError
  }

  get state(): AccessState {
    if (subscriptionActive(this.subscription)) {
      return AccessState.TESTING
    } else {
      if (this.hasAccess) {
        return this.isLatest && this.configValid ? AccessState.SUCCESS : AccessState.UPDATE_TEMPLATE
      } else {
        return AccessState.FAILED
      }
    }
  }

  public validateAccess() {
    if (this.subscription && this.subscription.closed) {
      this.subscription.unsubscribe()
    }
    this._hasApiError = false
    this.subscription = this.apiService
      .testSingleSyncPairAccess(this.requestDto, this.type)
      .pipe(
        finalize(() => {
          this.subscription = null
          this.testCount++
        })
      )
      .subscribe(
        (awsAccess) => {
          this.updateStatus(awsAccess)
        },
        (error) => {
          this._hasApiError = true
          handleArpioError(window.location.href, this.apiService, this.uiService, {
            errorContext: { dialogId: this.dialogId },
          })(error)
        }
      )
  }
}
